import { useState, useCallback, ReactNode } from 'react';
import type { MessageConfig, MessageType } from '../components/twui/Message';

// 全局挂载的添加消息函数类型
type AddMessageFunc = (msg: MessageConfig) => void;
let globalAddMessage: AddMessageFunc | null = null;

// 定义对外暴露的 API 参数类型（排除 id，因为 id 是内部生成的）
type MessageOptions = Omit<MessageConfig, 'id' | 'type'>;

// 提供给外部调用的 API
export const message = {
  // 内部处理函数，统一格式化参数
  _open: (type: MessageType, content: ReactNode | MessageOptions, options?: MessageOptions) => {
    let config: MessageConfig;
    
    // 如果 content 是对象且不是 React 节点，说明用户直接传了配置
    if (typeof content === 'object' && content !== null && !('$$typeof' in content)) {
      config = content as MessageOptions;
    } else {
      config = { content: content as ReactNode, ...options };
    }
    
    globalAddMessage?.({ type, duration: 3000, ...config });
  },
  
  success: (content: ReactNode | MessageOptions, options?: MessageOptions) => message._open('success', content, options),
  error: (content: ReactNode | MessageOptions, options?: MessageOptions) => message._open('error', content, options),
  info: (content: ReactNode | MessageOptions, options?: MessageOptions) => message._open('info', content, options),
  warning: (content: ReactNode | MessageOptions, options?: MessageOptions) => message._open('warning', content, options),
};

// 在 App 根组件中使用的 Hook
export const useMessage = () => {
  const [messages, setMessages] = useState<MessageConfig[]>([]);

  const addMessage = useCallback((msg: MessageConfig) => {
    const id = Date.now() + Math.random();
    setMessages((prev) => [...prev, { ...msg, id }]);
  }, []);

  const removeMessage = useCallback((id: string | number) => {
    setMessages((prev) => prev.filter((msg) => msg.id !== id));
  }, []);

  // 将 addMessage 挂载到全局对象上，供外部 API 调用
  globalAddMessage = addMessage;

  return { messages, removeMessage };
};